// Route map. Two route trees behind role gates: /office (desktop) and /engineer
// (mobile PWA), plus /login and a root redirect.
import { Navigate, createBrowserRouter, type RouteObject } from "react-router-dom";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { RootRedirect } from "./routes/RootRedirect";
import { Login } from "./routes/Login";
import { NotFound } from "./routes/NotFound";
import { OfficeLayout } from "./routes/office/OfficeLayout";
import { OfficeDashboard } from "./routes/office/OfficeDashboard";
import { CustomersPage } from "./routes/office/register/CustomersPage";
import { CustomerDetailPage } from "./routes/office/CustomerDetailPage";
import { SitesPage } from "./routes/office/register/SitesPage";
import { SiteDetailPage } from "./routes/office/SiteDetailPage";
import { AssetsPage } from "./routes/office/register/AssetsPage";
import { LiftDetailPage } from "./routes/office/LiftDetailPage";
import { PortfolioMapPage } from "./routes/office/PortfolioMapPage";
// The Planner (DispatchBoard) is retired from the nav (Session O); /office/schedule now
// redirects to the Scheduling calendar. The component file was deleted as dead code (Session
// AQS, X-6: unrouted, zero non-test importers, confirmed via git log at server/src/modules/
// scheduling/scheduling.routes.ts if its retired routes are ever removed too).
import { SchedulingPage } from "./routes/office/SchedulingPage";
import { PpmDashboardPage } from "./routes/office/PpmDashboardPage";
import { PpmPaymentsPage } from "./routes/office/PpmPaymentsPage";
import { QuotesPage } from "./routes/office/QuotesPage";
import { QuoteHubPage } from "./routes/office/QuoteHubPage";
import { PortfoliosPage, PortfolioDetailPage } from "./routes/office/PortfoliosPage";
import { QuoteCalculatorPage } from "./routes/office/QuoteCalculatorPage";
import { QuoteAnalyticsPage } from "./routes/office/QuoteAnalyticsPage";
import { CompliancePage } from "./routes/office/CompliancePage";
import { ComplianceReportsPage } from "./routes/office/ComplianceReportsPage";
import { ComplianceDocumentsPage } from "./routes/office/ComplianceDocumentsPage";
import { ComplaintsPage } from "./routes/office/ComplaintsPage";
import { InternalAuditsPage } from "./routes/office/InternalAuditsPage";
import { ActivityLogPage } from "./routes/office/ActivityLogPage";
// PersonnelCompliancePage and EngineerCompliancePage are no longer routed directly (Session ALU):
// PersonnelPage hosts both as the Office and Engineer tabs of /office/personnel and imports them
// itself. Neither component changed; only the routes that reach them did.
import { ManagementReviewsPage } from "./routes/office/ManagementReviewsPage";
import { ComplaintDetailPage } from "./routes/office/ComplaintDetailPage";
import { EnvironmentalRecordsPage } from "./routes/office/EnvironmentalRecordsPage";
import { EnvironmentalRecordDetailPage } from "./routes/office/EnvironmentalRecordDetailPage";
import { EnvironmentalObjectiveDetailPage } from "./routes/office/EnvironmentalObjectiveDetailPage";
import { InvoicesPage } from "./routes/office/InvoicesPage";
import { BillingHubPage } from "./routes/office/BillingHubPage";
import { InvoiceDetailPage } from "./routes/office/InvoiceDetailPage";
import { ContractsPage } from "./routes/office/ContractsPage";
import { ContractDetailPage } from "./routes/office/ContractDetailPage";
import { AllowancesPage } from "./routes/office/AllowancesPage";
import { RenewalsPage } from "./routes/office/RenewalsPage";
import { ReportingDashboardPage } from "./routes/office/ReportingDashboardPage";
import { NpsFigures, NpsPage, NpsSurveyPage } from "./routes/office/NpsPage";
// Session SC7A (the calm menu): the new tabs. None is a menu item; each is a tab of a hub (src/lib/nav.ts).
import { AllJobsPage, RepairJobsPage } from "./routes/office/JobsListPage";
import { RepairEstimatesListPage } from "./routes/office/RepairEstimatesListPage";
import { LolerDuePage } from "./routes/office/LolerDuePage";
import { CalloutsPage } from "./routes/office/CalloutsPage";
import { CalloutHubPage } from "./routes/office/CalloutHubPage";
import { JobSheetsPage } from "./routes/office/JobSheetsPage";
import { ReleaseQueuePage } from "./routes/office/ReleaseQueuePage";
import { VisitPage } from "./routes/office/VisitPage";
import { DefectsPage } from "./routes/office/DefectsPage";
import { DefectDetailPage } from "./routes/office/DefectDetailPage";
import { LolerPage, InsurerItemsRedirect } from "./routes/office/LolerPage";
import { SafedTestsPage } from "./routes/office/SafedTestsPage";
import { LolerReportDetailPage } from "./routes/office/LolerReportDetailPage";
import { ProjectsPage } from "./routes/office/ProjectsPage";
import { ProjectDetailPage } from "./routes/office/ProjectDetailPage";
// Prospects, Pipeline, ProspectDetailPage, Prospecting (Find new leads) and Outreach are retired
// from ELE's nav (Session AKF, 18 Aug 2026, Director decision): confirmed dead, zero real usage,
// script-imported leads only. The five page files and their client api methods were DELETED by Session AUI
// (Director decision D291, 4 October 2026); the backend (Prospect model, the prospect, prospecting, outreach and
// Apollo modules, the Guvnor prospect tools) is untouched. In git history if outbound prospecting is ever wanted.
import { SettingsPage } from "./routes/office/SettingsPage";
import { PortalUsersPage } from "./routes/office/PortalUsersPage";
import { AuditorAccessPage } from "./routes/office/AuditorAccessPage";
import { ConnectedAiPage } from "./routes/office/ConnectedAiPage";
import { OfficeUsersPage } from "./routes/office/OfficeUsersPage";
import { MyAccountPage } from "./routes/office/MyAccountPage";
import { CoverageZonesPage } from "./routes/office/CoverageZonesPage";
import { PersonalDataPage } from "./routes/office/PersonalDataPage";
import { DataRetentionPage } from "./routes/office/DataRetentionPage";
// Session AUB (email system Phase 4): ONE Mail workspace replaces the Email engine, Enquiries, Emails to answer (inbox
// triage) and Work orders list pages. Their old addresses redirect to the matching workspace tab; the two DETAIL pages
// (an enquiry, an engine message) stay, as the full record pages the review card links to.
import MailWorkspacePage from "./routes/office/mail/MailWorkspacePage";
import { EmailEngineRedirect } from "./routes/office/mail/mailRedirects";
import { EnquiryDetailPage } from "./routes/office/EnquiryDetailPage";
// Email engine (Session ASO): one inbound message recorded with an outcome, and its drafts.
import { EmailEngineMessagePage } from "./routes/office/EmailEnginePage";
import ReplyTemplatesPage, { ReplyTemplateDetailPage, ReplyTemplateNewPage } from "./routes/office/ReplyTemplatesPage";
import { SlaReportPage } from "./routes/office/SlaReportPage";
import { LiftPerformancePage } from "./routes/office/LiftPerformancePage";
import { EngineersPage } from "./routes/office/EngineersPage";
import { TimesheetsPage } from "./routes/office/TimesheetsPage";
import { StandbyRotaPage } from "./routes/office/StandbyRotaPage";
import { LeavePage } from "./routes/office/LeavePage";
import { ExpensesPage } from "./routes/office/ExpensesPage";
import { LoneWorkerPage } from "./routes/office/LoneWorkerPage";
import { TimesheetDetailPage } from "./routes/office/TimesheetDetailPage";
// VanStockPage (the office all-engineers van inventory) is retired from ELE's nav (Session ALU,
// 24 Aug 2026, Director decision of 17 Aug: zero items entered in two months live, "not now").
// The component file stays on disk, unimported here; the van-stock module, its API routes and
// the engineer PWA's own van stock surface are untouched, and the per-engineer stock modal on
// Admin > Engineers (EngineersPage StockButton) is still the office's way in. Same white-label
// reasoning as Messages below.
import { DebtorsPage } from "./routes/office/DebtorsPage";
import { PersonnelPage } from "./routes/office/PersonnelPage";
import { SubcontractorsPage } from "./routes/office/SubcontractorsPage";
import { SubcontractorDetailPage } from "./routes/office/SubcontractorDetailPage";
// MessagesPage (the office-side internal messaging UI) was retired from ELE's nav in Session AKF
// (18 Aug 2026), then RESTORED 28 Sep 2026 (Director decision, reversing AKF): the office <->
// engineer switcher put an admin/office login on the engineer PWA's own Messages tab for the
// first time, and that tab correctly refuses to show a thread of its own there (it is the
// ENGINEER's personal thread) and points back at this page -- so it has to exist again. The
// Message model, its API routes and the engineer PWA thread (EngineerMessages, still imported
// below) were never touched by AKF; this is a pure route restore of a page that was already fully
// built and working.
import { MessagesPage } from "./routes/office/MessagesPage";
import { KnowledgePage } from "./routes/office/KnowledgePage";
import { CompanyDocumentsPage } from "./routes/office/CompanyDocumentsPage";
import { KnowledgeDetailPage } from "./routes/office/KnowledgeDetailPage";
import { TrainingPage } from "./routes/office/TrainingPage";
import { TrainingDetailPage } from "./routes/office/TrainingDetailPage";
import { TrainingReferenceLibraryPage } from "./routes/office/TrainingReferenceLibraryPage";
import { AccidentsPage } from "./routes/office/AccidentsPage";
import { AccidentDetailPage } from "./routes/office/AccidentDetailPage";
import { EnforcementActionsPage } from "./routes/office/EnforcementActionsPage";
import { EnforcementActionDetailPage } from "./routes/office/EnforcementActionDetailPage";
import { PurchaseOrdersPage } from "./routes/office/PurchaseOrdersPage";
import { ReportAccident } from "./routes/engineer/ReportAccident";
import { EngineerLayout } from "./routes/engineer/EngineerLayout";
import { EngineerLogin } from "./routes/engineer/EngineerLogin";
import { EngineerInstall } from "./routes/engineer/EngineerInstall";
import { OfflineJobs } from "./routes/engineer/OfflineJobs";
import { EngineerHome } from "./routes/engineer/EngineerHome";
import MyJobsPage from "./routes/engineer/MyJobsPage";
import TrainingSessionScreen from "./routes/engineer/TrainingSession";
import { EngineerDiary } from "./routes/engineer/EngineerDiary";
import {
  EngineerSiteList,
  EngineerSiteHistoryPage,
  EngineerLiftHistoryPage,
} from "./routes/engineer/SiteHistory";
import { EngineerJobSheetPage } from "./routes/engineer/JobSheetDetail";
import { MyPastVisitsPage } from "./routes/engineer/MyPastVisits";
import { EngineerMessages } from "./routes/engineer/EngineerMessages";
import { MyTimesheet } from "./routes/engineer/MyTimesheet";
import { MyExpenses } from "./routes/engineer/MyExpenses";
import { JobDetail } from "./routes/engineer/JobDetail";
import { FaultAssistant } from "./routes/engineer/FaultAssistant";
import { PopTagScanPage } from "./routes/engineer/PopTagScanPage";
import SafedHowItWorks from "./routes/engineer/SafedHowItWorks";
import { NextOfKin } from "./routes/engineer/NextOfKin";
import { CalendarFeed } from "./routes/engineer/CalendarFeed";
import { AuditorAccess } from "./routes/auditor/AuditorAccess";
import { AuditorLayout } from "./routes/auditor/AuditorLayout";
import { AcceptQuote } from "./routes/public/AcceptQuote";
import { RenewalAccept } from "./routes/public/RenewalAccept";
import { ProjectQuotationPage } from "./routes/public/ProjectQuotationPage";
import { AcceptProject } from "./routes/public/AcceptProject";
import { NfcServicePage } from "./routes/public/NfcServicePage";
import { EquipmentServicePage } from "./routes/public/EquipmentServicePage";
import { AttendanceTapPage } from "./routes/public/AttendanceTapPage";
import { NpsResponsePage } from "./routes/public/NpsResponsePage";
import { PrivacyNoticePage } from "./routes/public/PrivacyNoticePage";
import { RepairEstimatePage } from "./routes/public/RepairEstimatePage";
import { SubcontractorOnboarding } from "./routes/public/SubcontractorOnboarding";
import { SubcontractorPaymentDetails } from "./routes/public/SubcontractorPaymentDetails";
import { StaffAccessPage } from "./routes/public/StaffAccessPage";
import { PortalLogin } from "./routes/portal/PortalLogin";
import { SubcontractorLogin, SubcontractorPortal } from "./routes/subcontractor/SubcontractorPortal";
import { SubcontractorDocumentReviewPage } from "./routes/office/SubcontractorDocumentReviewPage";
import { PortalDashboard } from "./routes/portal/PortalDashboard";
import { PortalSiteDetail } from "./routes/portal/PortalSiteDetail";
import { PortalSitesPage } from "./routes/portal/PortalSites";
import { PortalHistoryPage, PortalCalloutsPage, PortalRepairsPage } from "./routes/portal/PortalHistory";
import { PortalDocumentsPage } from "./routes/portal/PortalDocuments";
import { PortalInvoicesPage } from "./routes/portal/PortalInvoices";
import { PortalTrainingPage } from "./routes/portal/PortalTraining";
import { PortalOpenWorkPage } from "./routes/portal/PortalOpenWork";
import { PortalReportFault } from "./routes/portal/PortalReportFault";

// The office (desktop) child routes. Pulled out as a named constant so the set of paths
// the router actually serves is derivable (officeRoutePaths) and the sidebar nav can be
// asserted against it -- guaranteeing every nav item resolves to a real page (no 404s).
export const officeChildren: RouteObject[] = [
  { index: true, element: <OfficeDashboard /> },
  { path: "compliance", element: <CompliancePage /> },
  { path: "compliance/documents", element: <ComplianceDocumentsPage /> },
  { path: "company-documents", element: <CompanyDocumentsPage /> },
  { path: "compliance-reports", element: <ComplianceReportsPage officeControls /> },
  { path: "complaints", element: <ComplaintsPage /> },
  { path: "complaints/:id", element: <ComplaintDetailPage /> },
  // The accident / incident register (Session AOM). Office/admin; the API refuses anyone else,
  // and the auditor role is not granted it at all.
  { path: "accidents", element: <AccidentsPage /> },
  { path: "accidents/:id", element: <AccidentDetailPage /> },
  // HSE / Local Authority enforcement notices and prosecutions (Session AON), CHAS Q94's companion
  // register to the accident register above. Office/admin; the API refuses anyone else, and there is
  // no engineer-facing surface at all.
  { path: "enforcement-actions", element: <EnforcementActionsPage /> },
  { path: "enforcement-actions/:id", element: <EnforcementActionDetailPage /> },
  { path: "purchase-orders", element: <PurchaseOrdersPage /> },
  { path: "compliance/audits", element: <InternalAuditsPage /> },
  { path: "compliance/management-reviews", element: <ManagementReviewsPage /> },
  // Personnel (Session ALU): ONE page hosting both people registers as tabs. The two pages it
  // hosts are unchanged and still imported; only their ROUTES collapsed into this one. Both old
  // paths redirect to the tab they became rather than 404ing -- these are routes office staff
  // may have bookmarked, and the pages genuinely moved rather than disappeared (the same
  // treatment /office/reports and /office/schedule already get below).
  { path: "personnel", element: <PersonnelPage /> },
  { path: "personnel-compliance", element: <Navigate to="/office/personnel" replace /> },
  { path: "environmental-records", element: <EnvironmentalRecordsPage /> },
  { path: "environmental-records/:id", element: <EnvironmentalRecordDetailPage /> },
  { path: "environmental-objectives/:id", element: <EnvironmentalObjectiveDetailPage /> },
  // Retired Planner: redirect the old bookmark to the Scheduling calendar (no 404).
  { path: "schedule", element: <Navigate to="/office/scheduling" replace /> },
  { path: "scheduling", element: <SchedulingPage /> },
  { path: "ppm", element: <PpmDashboardPage /> },
  { path: "ppm-payments", element: <PpmPaymentsPage /> },
  { path: "quotes", element: <QuotesPage /> },
  { path: "quotes/new", element: <QuoteCalculatorPage /> },
  // Session AQW, Q-1 (the Quote Hub): a quote's own record page. React Router ranks the static
  // "quotes/new" segment above this dynamic one regardless of declaration order, so the two never
  // collide.
  { path: "quotes/:id", element: <QuoteHubPage /> },
  { path: "quote-analytics", element: <QuoteAnalyticsPage /> },
  { path: "quote-portfolios", element: <PortfoliosPage /> },
  { path: "quote-portfolios/:id", element: <PortfolioDetailPage /> },
  { path: "customers", element: <CustomersPage /> },
  { path: "customers/:id", element: <CustomerDetailPage /> },
  { path: "sites", element: <SitesPage /> },
  { path: "sites/:id", element: <SiteDetailPage /> },
  { path: "lifts", element: <AssetsPage /> },
  { path: "lifts/:id", element: <LiftDetailPage /> },
  { path: "map", element: <PortfolioMapPage /> },
  { path: "contracts", element: <ContractsPage /> },
  { path: "contracts/:id", element: <ContractDetailPage /> },
  { path: "allowances", element: <AllowancesPage /> },
  { path: "renewals", element: <RenewalsPage /> },
  // Monthly report retired (Session AKF): folded into Business analytics below, which now carries
  // its month-scoped PDF export and Revenue tile. A redirect, not a 404: the report genuinely
  // moved rather than disappeared, mirroring the retired Planner's own /office/schedule redirect.
  { path: "reports", element: <Navigate to="/office/reporting" replace /> },
  { path: "reporting", element: <ReportingDashboardPage /> },
  { path: "sla-report", element: <SlaReportPage /> },
  { path: "lift-performance", element: <LiftPerformancePage /> },
  { path: "nps", element: <NpsPage /> },
  // Session SC7A: the two halves of the satisfaction page as tabs (the survey you send under Customers, the figures under Reports).
  { path: "customer-satisfaction", element: <NpsSurveyPage /> },
  { path: "nps-figures", element: <NpsFigures /> },
  // Session SC7A: All jobs (Schedule hub), Repair jobs and Repair estimates (Repairs hub), LOLER Due dates (LOLER and SAFed hub).
  { path: "jobs", element: <AllJobsPage /> },
  { path: "repair-jobs", element: <RepairJobsPage /> },
  { path: "repair-estimates", element: <RepairEstimatesListPage /> },
  { path: "loler-due", element: <LolerDuePage /> },
  // Call log (the standalone page) retired (Session AKF): the per-customer Call log tab on the
  // customer record is unaffected and stays. No redirect target exists, so this 404s like every
  // other item this session retires with no successor page.
  { path: "invoices", element: <InvoicesPage /> },
  // Invoices due retired (Session AKF): recurring contract billing stays on Xero directly, never
  // the platform. No successor page, so this 404s.
  { path: "invoices/:id", element: <InvoiceDetailPage /> },
  { path: "debtors", element: <DebtorsPage /> },
  // Session ATB (Phase 4, D30, M-5): the billing hub, one home for the money side of the business.
  // Every route it links to (invoices, debtors, ppm-payments, allowances, timesheets, contracts)
  // is unchanged and stays reachable at its own path -- this is a navigation layer over them.
  { path: "billing", element: <BillingHubPage /> },
  { path: "callouts", element: <CalloutsPage /> },
  // Session AQW (Phase 2, W-6): the call-out's own record page, keyed by the CallOut id.
  { path: "callouts/:id", element: <CalloutHubPage /> },
  // Session AUB: the Mail workspace, and the four list pages it replaced, each redirected to its tab.
  { path: "mail", element: <MailWorkspacePage /> },
  { path: "inbox-triage", element: <Navigate to="/office/mail?tab=triage" replace /> },
  { path: "enquiries", element: <Navigate to="/office/mail?tab=reply" replace /> },
  { path: "work-orders", element: <Navigate to="/office/mail?tab=work" replace /> },
  { path: "enquiries/:id", element: <EnquiryDetailPage /> },
  // Restored 28 Sep 2026 (Director decision, reversing Session AKF's retirement); see the import comment above.
  { path: "messages", element: <MessagesPage /> },
  // Session AUB: the old engine page keeps its ?tab= (library, quality, history) and ?filter= on the way to Mail.
  { path: "email-engine", element: <EmailEngineRedirect /> },
  { path: "email-engine/:id", element: <EmailEngineMessagePage /> },
  // Session AUA (email system Phase 3): the reply template library, its proposals queue and the style guide.
  { path: "reply-templates", element: <ReplyTemplatesPage /> },
  { path: "reply-templates/new", element: <ReplyTemplateNewPage /> },
  { path: "reply-templates/:id", element: <ReplyTemplateDetailPage /> },
  { path: "jobsheets", element: <JobSheetsPage /> },
  // Scale target S1: the release and send queue. A static path, so it wins over jobsheets/:id.
  { path: "jobsheets/release", element: <ReleaseQueuePage /> },
  // Session AQW (Phase 2, W-7): the visit's own record page, keyed by the Job id (the jobsheet's
  // own jobId, matching what GET /api/jobsheets/:jobId already expects).
  { path: "jobsheets/:id", element: <VisitPage /> },
  { path: "knowledge", element: <KnowledgePage /> },
  { path: "knowledge/:id", element: <KnowledgeDetailPage /> },
  // On-site training records (Session AFL): the list (filterable, and the entry point the site
  // page links into with ?siteId=) and one session's detail page.
  { path: "training", element: <TrainingPage /> },
  { path: "training/:id", element: <TrainingDetailPage /> },
  // The training reference document library (Session AFP): the material behind each training type.
  { path: "training-documents", element: <TrainingReferenceLibraryPage /> },
  // Van stock retired from the office nav (Session ALU): no successor office page, so this 404s
  // like the AKF retirements. Reachable per engineer at Admin > Engineers, which is where the
  // morning-action "Van stock reorder" link now points.
  { path: "defects", element: <DefectsPage /> },
  { path: "defects/:id", element: <DefectDetailPage /> },
  { path: "loler", element: <LolerPage /> },
  { path: "loler/:id", element: <LolerReportDetailPage /> },
  // Session BDC: the SAFed worklist, in the Compliance group beside LOLER reports.
  { path: "safed", element: <SafedTestsPage /> },
  // Session BAQ: the examiner's LOLER report items, worked to closure. Session BDF: now the "To close" tab of LOLER.
  { path: "insurer-items", element: <InsurerItemsRedirect /> },
  { path: "insurer-items/*", element: <InsurerItemsRedirect /> },
  { path: "projects", element: <ProjectsPage /> },
  { path: "projects/:id", element: <ProjectDetailPage /> },
  // Pipeline, Prospects (+ its detail page), Prospecting and Outreach retired (Session AKF):
  // confirmed dead, zero real usage. No successor pages, so all four 404.
  { path: "portal-users", element: <PortalUsersPage /> },
  // Auditor access is the magic-link generator (Session AEJ, D29). The old password-account page
  // (AuditorsPage) and its three /api/admin/auditors routes were DELETED in Session AJI: that flow
  // emailed an external assessor a plaintext temporary password with no review step. The nav slot
  // ("Auditor access") is unchanged and has always pointed here.
  { path: "auditors", element: <AuditorAccessPage /> },
  { path: "connected-ai", element: <ConnectedAiPage /> },
  { path: "engineers", element: <EngineersPage /> },
  { path: "standby", element: <StandbyRotaPage /> },
  // Session ASZ (D-V1): the minimum HR/leave record the standby rota and dispatch warn against.
  { path: "leave", element: <LeavePage /> },
  // Session ATE: engineer mileage/materials/other expense claims, review/approve/reject.
  { path: "expenses", element: <ExpensesPage /> },
  // Session AOB. The path is also the click destination of LONE_WORKER_OVERDUE_PUSH_TO_OFFICE, so
  // pushPayloadRoutes.test.ts scans this file and fails if the two ever part company.
  { path: "lone-worker", element: <LoneWorkerPage /> },
  { path: "timesheets", element: <TimesheetsPage /> },
  { path: "timesheets/:engineerId/:weekStarting", element: <TimesheetDetailPage /> },
  { path: "engineer-compliance", element: <Navigate to="/office/personnel?tab=engineer" replace /> },
  { path: "office-users", element: <OfficeUsersPage /> },
  { path: "subcontractors", element: <SubcontractorsPage /> },
  // Session AUZ: the office review of subcontractor documents (submitted, not accepted, expired, missing). Static, so it
  // is matched ahead of the :id route below.
  { path: "subcontractors/documents", element: <SubcontractorDocumentReviewPage /> },
  { path: "subcontractors/:id", element: <SubcontractorDetailPage /> },
  { path: "coverage-zones", element: <CoverageZonesPage /> },
  // Personal data requests (Session ASQ, Stream G): find a person, confirm their records, download the subject access
  // export. Admin only (the nav item is adminOnly and every API route behind the page refuses anyone else).
  { path: "personal-data", element: <PersonalDataPage /> },
  // Data retention (Session ASQ, Stream G, Item 4): the weekly sweep proposals, confirmed per rule. Admin only (the nav
  // item is adminOnly and every API route behind the page refuses anyone else).
  { path: "data-retention", element: <DataRetentionPage /> },
  { path: "activity", element: <ActivityLogPage /> },
  { path: "settings", element: <SettingsPage /> },
  // Session SC7B: one Settings section at a time, in the address (/office/settings/email). An address that names no
  // section shows the first, and the bare /office/settings opens it.
  { path: "settings/:section", element: <SettingsPage /> },
  // The signed-in person's own account (Session ASB): where they are signed in. Reached from the
  // "My account" link beside Sign out, not from the sidebar, because it is about the person, not the business.
  { path: "account", element: <MyAccountPage /> },
];

// The full office paths the router serves (the index route resolves to /office), excluding
// the parameterised detail routes (:id) which are reached by link, not by the sidebar.
export function officeRoutePaths(): string[] {
  return officeChildren
    .map((r) => (r.index ? "/office" : r.path ? `/office/${r.path}` : null))
    .filter((p): p is string => p !== null && !p.includes(":"));
}

export const router = createBrowserRouter([
  { path: "/", element: <RootRedirect /> },
  { path: "/login", element: <Login /> },
  // Auditor magic-link access (Session AEJ, D29). The office-generated link lands on the PUBLIC
  // /auditor/access/:token page, which validates the token and issues the auditor session. The
  // old email+password login page is retired: /auditor-login now redirects to the same access
  // page with the "invalid" sentinel, so an old bookmark reaches a helpful "contact ELE" page
  // rather than a 404.
  { path: "/auditor/access/:token", element: <AuditorAccess /> },
  { path: "/auditor-login", element: <Navigate to="/auditor/access/invalid" replace /> },
  // Public, no-auth: the customer accept page (ESA link in the quote PDF).
  { path: "/quote/:token", element: <AcceptQuote /> },
  // Public, no-auth: the customer contract-renewal page (Session AII, Contract.renewalToken).
  { path: "/renewal/:token", element: <RenewalAccept /> },
  // Public, no-auth: the project quotation HTML page (Session ABN Item 3; the covering email
  // links here -- the primary customer-facing quotation format).
  { path: "/project-quotation/:token", element: <ProjectQuotationPage /> },
  // Public, no-auth: the modernisation ACCEPTANCE page (Session AEI, D1). The acceptance-link
  // email lands here; the customer reads the quotation + full Conditions of Contract, then signs.
  { path: "/accept/project/:token", element: <AcceptProject /> },
  // Public, no-auth: the RFID service page each lift's tag resolves to.
  { path: "/nfc/:token", element: <NfcServicePage /> },
  // Public, no-auth: the RFID service page each piece of care equipment's tag resolves to
  // (Session ACT). Same /nfc route family, its own /e/ sub-path + page so the locked lift page
  // stays byte-untouched; the token is the same unguessable bearer credential.
  { path: "/nfc/e/:token", element: <EquipmentServicePage /> },
  // Public, no-auth: the INTERNAL attendance tap (Session AMZ). Its OWN top-level path, never under
  // /nfc, because it is a second physical tag with a second token space: a tap token does not exist
  // in the nfcToken column and vice versa, so neither URL can ever resolve the other's page. This
  // page GET records nothing; it POSTs to record on open, so a link preview of a tag URL cannot
  // fabricate an attendance.
  { path: "/tap/:token", element: <AttendanceTapPage /> },
  // Public, no-auth: the post-service NPS survey response page.
  { path: "/nps/:token", element: <NpsResponsePage /> },
  // Public, no-auth: the privacy notice an admin publishes in Settings (Session ASQ, Stream G, Item 5). Until one is
  // saved the page shows the ordinary not-found page, and every link to it stays hidden.
  { path: "/privacy", element: <PrivacyNoticePage /> },
  // Public, no-auth: the customer's repair estimate page, approve or decline online (Session ARS, D91).
  { path: "/estimate/:token", element: <RepairEstimatePage /> },
  // Public, no-auth: subcontractor self-service onboarding (one-time invite link).
  { path: "/subcontractor-onboarding/:token", element: <SubcontractorOnboarding /> },
  // Session ARZ: the one-time "Request payment details" link (no login).
  { path: "/subcontractor-payment-details/:token", element: <SubcontractorPaymentDetails /> },
  // Customer portal: passwordless magic-link auth (the pages enforce access via the
  // portal API, which returns 401 when the portal session cookie is absent).
  { path: "/portal/login", element: <PortalLogin /> },
  // Session ATA: the Subcontractor Portal, its own emailed-code sign-in and a read-only view of the jobs and hours of
  // the engineers the office linked to the subcontractor. Access is enforced by its API (401 sends it back to sign in).
  { path: "/subcontractor/login", element: <SubcontractorLogin /> },
  { path: "/subcontractor", element: <SubcontractorPortal /> },
  // Session AHH: the magic link this page used to verify is retired (6-digit codes replace it, and
  // the login page itself now handles code entry). A pre-deploy link still floating in an inbox
  // lands here and is sent straight to sign-in rather than a dead verify screen.
  { path: "/portal/verify", element: <Navigate to="/portal/login" replace /> },
  // Public, no-auth: the staff RFID sign-in landing page (Session AFH). An engineer or office user
  // requests a link from a lift's RFID page; clicking it validates the token, sets the session
  // cookie and redirects to that lift's record.
  { path: "/staff-access/:token", element: <StaffAccessPage /> },
  // Customer portal top-level sections (Session AFH Jobber-hub redesign): Overview, the two
  // cross-site history feeds, Documents and the Sites list, plus the per-site detail page. Access
  // is enforced by the portal API (401 when the portal session cookie is absent), which each page
  // handles by sending the customer to /portal/login.
  { path: "/portal", element: <PortalDashboard /> },
  { path: "/portal/history", element: <PortalHistoryPage /> },
  { path: "/portal/callouts", element: <PortalCalloutsPage /> },
  // Session AMX: repairs get their own top-level section (previously reachable only by opening a site).
  { path: "/portal/repairs", element: <PortalRepairsPage /> },
  { path: "/portal/documents", element: <PortalDocumentsPage /> },
  // Phase 8 (Session ATJ, M-9a): the full invoices view, reached from the dashboard's outstanding-
  // invoices card ("See all invoices"), deliberately NOT an eighth top-level nav tab (PortalShell's
  // own bottom bar is a measured seven-item ceiling).
  { path: "/portal/invoices", element: <PortalInvoicesPage /> },
  // Session AUD (email system Phase 5, Items 3 and 6): open work, and reporting a fault on one of the
  // customer's lifts. Reached from the Overview and each lift card, not new nav tabs (PortalShell's
  // seven-item ceiling).
  { path: "/portal/work", element: <PortalOpenWorkPage /> },
  { path: "/portal/report-fault", element: <PortalReportFault /> },
  // Training as a top-level section (Session AGJ): every completed session across all their sites,
  // so a customer with several buildings does not have to open each site's tab in turn.
  { path: "/portal/training", element: <PortalTrainingPage /> },
  { path: "/portal/sites", element: <PortalSitesPage /> },
  { path: "/portal/sites/:id", element: <PortalSiteDetail /> },
  {
    path: "/office",
    element: (
      <ProtectedRoute allow={["admin", "office"]}>
        <OfficeLayout />
      </ProtectedRoute>
    ),
    children: officeChildren,
  },
  // Engineer sign-in (email + emailed code, Session BCE) -- separate from the office Google sign-in.
  { path: "/engineer/login", element: <EngineerLogin /> },
  { path: "/engineer/install", element: <EngineerInstall /> },
  // Session AFW: a read-only, UNGUARDED view of the engineer's cached jobs, reached from the login
  // screen's "Continue offline" link so a poor-signal engineer is never locked out of their job
  // details. Reads IndexedDB directly; no auth, no submit.
  { path: "/engineer/offline", element: <OfflineJobs /> },
  {
    path: "/engineer",
    element: (
      <ProtectedRoute allow={["admin", "engineer"]} loginPath="/engineer/login">
        <EngineerLayout />
      </ProtectedRoute>
    ),
    children: [
      { index: true, element: <EngineerHome /> },
      // My Jobs (Session AFF): the full job list, moved off the home dashboard onto its own tab.
      { path: "jobs", element: <MyJobsPage /> },
      { path: "diary", element: <EngineerDiary /> },
      // The engineer's own weekly timesheet (Session AEL, D41): view only, submit for approval.
      { path: "timesheet", element: <MyTimesheet /> },
      { path: "messages", element: <EngineerMessages /> },
      // Session ATE: submit a mileage/materials/other expense, general or against a job (?jobId=).
      { path: "expenses", element: <MyExpenses /> },
      { path: "jobs/:id", element: <JobDetail /> },
      // Deliver a training session on site (Session AGG): the delivery checklist, the attendees and
      // their signatures, then the certificate. Live-only; deliberately not cached offline, because
      // the certificate is numbered and issued server-side on submission.
      { path: "training/:id", element: <TrainingSessionScreen /> },
      // Fault Assistant (Session AFC): ask about a fault, drawing on the knowledge base and this
      // portfolio's history. Entered from a call-out job (?jobId), a lift record (?assetId), or the
      // home screen (no context). Live-only; never queued offline.
      { path: "fault-assistant", element: <FaultAssistant /> },
      // Scan a Guv POP Tag from inside the app (Session AND). The motor-room sticker opened by the
      // phone's OS lands on the public /tap/<token> page instead, and records without a name far
      // more often than anyone realised, because the OS browser's cookie jar need not be this app's.
      // Live-only and deliberately NEVER queued through the offline outbox: the tap route stamps its
      // own instant and never reads the body, so a tap drained at street level forty minutes later
      // would record a time the engineer was not there.
      { path: "pop-tag", element: <PopTagScanPage /> },
      // Session BDB: the short engineer guide to a SAFed day (static, readable offline).
      { path: "safed-how-to", element: <SafedHowItWorks /> },
      // Report an accident or a near miss from the field (Session AOM). WRITE ONLY: it posts and
      // confirms, and there is no engineer-side screen anywhere that reads an accident record
      // back. Live-only and deliberately NOT queued through the offline outbox; the form keeps a
      // local draft so a failed send never loses what was typed.
      { path: "report-accident", element: <ReportAccident /> },
      // The engineer's own next-of-kin fallback contact (Session AOU), reached from a QuietLink on
      // the home screen. Gated on hasOwnEngineerRecords inside the component itself.
      { path: "next-of-kin", element: <NextOfKin /> },
      // The engineer's own jobs and standby weeks in their own calendar app (Session ATY), reached from a QuietLink on
      // the home screen. Gated on hasOwnEngineerRecords inside the component itself. Online only: the link exists only
      // in the reply that makes it.
      { path: "calendar-feed", element: <CalendarFeed /> },
      // Site and lift history (Session AEK, D33): read-only, so an engineer can see what a previous
      // engineer found on a lift they have not attended before. Since Session ASB (D-SEC-6) only the
      // sites where they hold open work.
      { path: "history", element: <EngineerSiteList /> },
      { path: "history/sites/:siteId", element: <EngineerSiteHistoryPage /> },
      { path: "history/lifts/:assetId", element: <EngineerLiftHistoryPage /> },
      // One completed visit in full (Session AEM): the record behind a history row, not the
      // one-line summary. Keyed on the job id, which is what the history rows carry.
      { path: "history/visits/:jobId", element: <EngineerJobSheetPage /> },
      // The engineer's OWN completed visits (Session ASB, D-SEC-6): the way back to a record once its
      // site has left the site list, since they keep their own past jobs for good.
      { path: "history/mine", element: <MyPastVisitsPage /> },
    ],
  },
  // Auditor (external assessor) portal: a read-only route tree, gated to the auditor role and
  // bouncing an unauthenticated visitor to the access page (which shows the "contact ELE" state
  // for the invalid sentinel). It reuses the K1 compliance-reports hub with hideGeneratedBy (no
  // staff names) inside a minimal branded layout -- no office nav, no operational data.
  {
    path: "/auditor",
    element: (
      <ProtectedRoute allow={["auditor"]} loginPath="/auditor/access/invalid">
        <AuditorLayout />
      </ProtectedRoute>
    ),
    children: [
      { index: true, element: <Navigate to="/auditor/compliance-reports" replace /> },
      { path: "compliance-reports", element: <ComplianceReportsPage hideGeneratedBy /> },
    ],
  },
  { path: "*", element: <NotFound /> },
]);
